import { useEffect, useCallback, useRef } from 'react';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useRoomContext,
  useTracks,
  useParticipants,
  useLocalParticipant,
} from '@livekit/components-react';
import { Track, RoomEvent, DisconnectReason } from 'livekit-client';
import '@livekit/components-styles';
import useVideoStore from '../../stores/videoStore';
import useClassroomStore from '../../stores/classroomStore';
import { createBlurredVideoTrack } from '../../utils/backgroundBlur';
import VideoParticipant from './VideoParticipant';

/**
 * Inner component that uses LiveKit hooks (must be inside LiveKitRoom)
 */
const VideoRoomInner = ({ onParticipantsChange, initialCameraEnabled = true, initialMicEnabled = true }) => {
  const room = useRoomContext();
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const setConnected = useVideoStore(s => s.setConnected);
  const setRemoteParticipants = useVideoStore(s => s.setRemoteParticipants);
  const publishedRef = useRef(false);

  // Handle room connection
  useEffect(() => {
    if (room) {
      const handleConnected = () => {
        setConnected(true);
      };

      const handleDisconnected = () => {
        setConnected(false);
        publishedRef.current = false;
      };

      room.on(RoomEvent.Connected, handleConnected);
      room.on(RoomEvent.Disconnected, handleDisconnected);

      // Check if already connected
      if (room.state === 'connected') {
        handleConnected();
      }

      return () => {
        room.off(RoomEvent.Connected, handleConnected);
        room.off(RoomEvent.Disconnected, handleDisconnected);
      };
    }
  }, [room, setConnected]);

  // Publish camera + mic as user-provided MediaStreamTracks. LiveKit's auto-publish
  // (video/audio props on <LiveKitRoom>) creates SDK-managed tracks that stop the
  // underlying getUserMedia stream on every mute and re-acquire it on unmute —
  // causing a fresh permission prompt each toggle on Safari and the SFU briefly
  // forwarding the unmute-restart stream before the UI catches up.
  // User-provided tracks let mute/unmute flip a flag without touching getUserMedia.
  useEffect(() => {
    if (!room || !localParticipant) return;
    if (publishedRef.current) return;

    let cancelled = false;
    let rawVideoTrack = null;
    let audioTrack = null;
    let blurHandle = null;
    let onConnected = null;

    const publish = async () => {
      if (room.state !== 'connected') return;
      publishedRef.current = true;
      // Respect the student's toggles from CameraPreviewModal — only request the
      // hardware they left enabled. Requesting a disabled device would light the
      // browser indicator and re-open the camera/mic the user just declined.
      if (!initialCameraEnabled && !initialMicEnabled) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: initialCameraEnabled,
          audio: initialMicEnabled,
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        rawVideoTrack = stream.getVideoTracks()[0];
        audioTrack = stream.getAudioTracks()[0];

        // Decision on background blur is made in CameraPreviewModal and
        // persisted; read once here so a mid-lesson toggle doesn't re-run
        // getUserMedia (which would flash the browser permission indicator).
        const wantBlur = useClassroomStore.getState().blurEnabled;
        let videoTrackToPublish = rawVideoTrack;
        if (rawVideoTrack && wantBlur) {
          try {
            blurHandle = await createBlurredVideoTrack(rawVideoTrack);
            if (cancelled) {
              try { blurHandle?.dispose(); } catch (_) {}
              blurHandle = null;
            } else if (blurHandle?.track) {
              videoTrackToPublish = blurHandle.track;
            }
          } catch (e) {
            // Fall back to publishing the raw (unblurred) track so the user
            // still gets video — better than silently going black.
            console.error('[VideoRoom] Background blur failed, publishing raw track:', e);
            blurHandle = null;
          }
        }

        if (videoTrackToPublish && !localParticipant.getTrackPublication(Track.Source.Camera)) {
          await localParticipant.publishTrack(videoTrackToPublish, {
            source: Track.Source.Camera,
          });
        }
        if (audioTrack && !localParticipant.getTrackPublication(Track.Source.Microphone)) {
          await localParticipant.publishTrack(audioTrack, {
            source: Track.Source.Microphone,
          });
        }
      } catch (err) {
        console.error('[VideoRoom] Failed to publish user media:', err);
        publishedRef.current = false;
      }
    };

    if (room.state === 'connected') {
      publish();
    } else {
      onConnected = () => publish();
      room.once(RoomEvent.Connected, onConnected);
    }

    return () => {
      cancelled = true;
      if (onConnected) room.off(RoomEvent.Connected, onConnected);
      // Tear down the blur pipeline first (stops rAF loop + canvas track),
      // then release the underlying camera/mic hardware. LiveKit unpublishes
      // on disconnect but does not stop tracks it didn't create.
      if (blurHandle) {
        try { blurHandle.dispose(); } catch (_) {}
      }
      if (rawVideoTrack) rawVideoTrack.stop();
      if (audioTrack) audioTrack.stop();
    };
  }, [room, localParticipant]);

  // Update participants in store
  useEffect(() => {
    const remoteParticipants = participants.filter(p => !p.isLocal);
    setRemoteParticipants(remoteParticipants);

    // Callback for parent component
    if (onParticipantsChange) {
      onParticipantsChange(participants, localParticipant);
    }
  }, [participants, localParticipant, setRemoteParticipants, onParticipantsChange]);

  return null; // This component just manages state
};

/**
 * Video participant tile for the gallery
 */
export const VideoTile = ({ participant, isLocal = false, className = '' }) => {
  const tracks = useTracks([Track.Source.Camera], {
    onlySubscribed: !isLocal,
  }).filter(track => track.participant.identity === participant?.identity);

  const videoTrack = tracks.find(t => t.source === Track.Source.Camera);

  return (
    <VideoParticipant
      participant={participant}
      videoTrack={videoTrack}
      isLocal={isLocal}
      className={className}
    />
  );
};

// Connection-level drops worth one automatic re-join with a fresh token.
// Deliberate ones (left, kicked, room deleted, opened in another tab) are not.
const RETRYABLE_DISCONNECT_REASONS = new Set([
  DisconnectReason.UNKNOWN_REASON,
  DisconnectReason.SERVER_SHUTDOWN,
  DisconnectReason.STATE_MISMATCH,
  DisconnectReason.JOIN_FAILURE,
  DisconnectReason.SIGNAL_CLOSE,
  DisconnectReason.CONNECTION_TIMEOUT,
]);
const RETRY_COOLDOWN_MS = 30 * 1000;

/**
 * Main VideoRoom component
 * Wraps LiveKitRoom and provides video functionality
 */
const VideoRoom = ({
  token,
  serverUrl,
  onConnected,
  onDisconnected,
  onParticipantsChange,
  children,
  connectOptions = {},
  initialCameraEnabled = true,
  initialMicEnabled = true,
}) => {
  const disconnect = useVideoStore(s => s.disconnect);
  const fetchToken = useVideoStore(s => s.fetchToken);
  const lastRetryAtRef = useRef(0);
  const retryInFlightRef = useRef(false);

  // On a failed connect / dropped connection, ask the backend for a fresh token
  // once (it re-probes LiveKit and may move us to the backup project if the
  // primary just ran out of minutes). Returns true if a retry was started.
  const retryWithFreshToken = useCallback(() => {
    if (retryInFlightRef.current) return true;
    const { lessonData } = useVideoStore.getState();
    if (!lessonData || Date.now() - lastRetryAtRef.current < RETRY_COOLDOWN_MS) return false;
    lastRetryAtRef.current = Date.now();
    retryInFlightRef.current = true;
    fetchToken(lessonData, { recheck: true }).then((result) => {
      retryInFlightRef.current = false;
      if (!result.success) {
        disconnect();
        if (onDisconnected) onDisconnected();
      }
    });
    return true;
  }, [fetchToken, disconnect, onDisconnected]);

  const handleConnected = useCallback(() => {
    if (onConnected) onConnected();
  }, [onConnected]);

  const handleDisconnected = useCallback((reason) => {
    if (RETRYABLE_DISCONNECT_REASONS.has(reason) && retryWithFreshToken()) return;
    if (retryInFlightRef.current) return;
    disconnect();
    if (onDisconnected) onDisconnected();
  }, [disconnect, onDisconnected, retryWithFreshToken]);

  const handleError = useCallback((error) => {
    console.error('LiveKit connection error:', error);
    retryWithFreshToken();
  }, [retryWithFreshToken]);

  if (!token || !serverUrl) {
    return null;
  }

  return (
    <LiveKitRoom
      token={token}
      serverUrl={serverUrl}
      connect={true}
      connectOptions={{
        autoSubscribe: true,
        ...connectOptions,
      }}
      onConnected={handleConnected}
      onDisconnected={handleDisconnected}
      onError={handleError}
      video={false}
      audio={false}
      onMediaDeviceFailure={(error) => {
        console.error('[VideoRoom] Media device failure:', error);
      }}
    >
      <RoomAudioRenderer />
      <VideoRoomInner
        onParticipantsChange={onParticipantsChange}
        initialCameraEnabled={initialCameraEnabled}
        initialMicEnabled={initialMicEnabled}
      />
      {children}
    </LiveKitRoom>
  );
};

export default VideoRoom;
